import {
  getOrganizationsByOrgIdUsers,
  getOrganizationsByOrgIdUsersOptions,
} from "@aura-frontend/omnibus-az-client";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import {
  Button,
  cn,
  Icons,
  Input,
  Label,
  Table,
  useDebounce,
} from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import { removeOrgPrefix } from "@/lib/utils";
import { useManageLayoutContext } from "@/pages/Manage/ManageLayout";

import CreateRoleModal from "./components/CreateRoleModal";
import { useColumns } from "./Operators.columns";

const usersInfiniteQueryOptions = (orgId: string, search?: string) =>
  infiniteQueryOptions({
    queryKey: ["getOrganizationsByOrgIdUsers", orgId, search],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getOrganizationsByOrgIdUsers({
        path: { org_id: encodeURIComponent(orgId) },
        query: { continue: pageParam, search },
        signal,
        throwOnError: true,
      });
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.continue,
    enabled: !!orgId,
  });

const Operators = () => {
  const { orgId: rawOrgId } = useManageLayoutContext();
  const orgId = rawOrgId ? removeOrgPrefix(rawOrgId) : "";
  const [isRoleCreationModalOpen, setIsRoleCreationModalOpen] = useState(false);
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const [rawSearchValue, setSearchValue] = useState("");
  const searchValue = useDebounce(rawSearchValue, 250, false);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(usersInfiniteQueryOptions(orgId, searchValue));

  const { data: hasAccountOwner = false, isLoading: isLoadingAccountOwner } =
    useQuery({
      ...getOrganizationsByOrgIdUsersOptions({
        path: {
          org_id: encodeURIComponent(orgId) ?? "",
        },
        query: {
          roles: "account_owner,unverified_account_owner",
        },
      }),
      enabled: !!orgId,
      select: (response) => response.users?.length > 0,
    });

  const users = useMemo(
    () => data?.pages.flatMap((page) => page.users) ?? [],
    [data?.pages],
  );

  const { columns } = useColumns();

  return (
    <div className="relative flex flex-col">
      <div
        className={cn(
          "flex flex-col-reverse px-5 gap-4 items-start",
          "md:flex-row md:items-end md:justify-between",
        )}
      >
        <div>
          <Label htmlFor="operator-search" className="mb-2 text-sm">
            Search
          </Label>

          <Input
            placeholder="Look for an operator"
            className="w-60"
            name="operator-search"
            id="operator-search"
            inputMode="text"
            type="text"
            value={rawSearchValue}
            isClearable={true}
            onClear={() => {
              setSearchValue("");
            }}
            iconRight={
              <Icons.MagnifyingGlass className="text-placeholder h-5 w-5" />
            }
            onChange={(e) => {
              if (e.target.value === "") {
                setSearchValue("");
                return;
              }

              setSearchValue(e.target.value);
            }}
          />
        </div>

        <Button
          variant="secondary-flat-light"
          size="sm"
          onClick={() => setIsRoleCreationModalOpen(true)}
        >
          <Icons.Plus className="size-4" />
          Create New
        </Button>
      </div>

      {!isLoadingAccountOwner && (
        <CreateRoleModal
          open={isRoleCreationModalOpen}
          onOpenChange={setIsRoleCreationModalOpen}
          hasAccountOwner={hasAccountOwner}
          organizationId={orgId}
        />
      )}

      <div className="mt-8">
        <Table
          enableHorizontalScroll
          variant="infinite-scroll"
          hasMore={hasNextPage}
          isFetching={isLoading || isFetchingNextPage || isLoadingAccountOwner}
          data={users}
          columns={columns}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          rowClassName={"shadow-glow-tiny rounded"}
          rowsWrapperClassName="gap-y-4"
          tableWrapperClassName="overflow-auto px-5 py-1"
        />
      </div>
    </div>
  );
};

export { Operators };
